import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getEnvSettings } from '../hostBasedSettings';
import { IBlenderHub, IHandlers, Method, updateConnectionStatus, updateMessageState } from '../../redux/slices/consumeBlenderHubSlice';
import { useAppDispatch } from '../../redux/hooks';
import { selectAuth } from '../../redux/slices/authSlice';

const ConsumeBlenderHub = ({
    onGettingStatus,
    onStateTransitions
}: IHandlers): IBlenderHub => {

    const dispatch = useAppDispatch();

    const { token } = useSelector(
        selectAuth
    );

    const blenderAgentUrl = getEnvSettings().blenderUrl + '/agentHub'; 

    const [connection, setConnection] = useState<HubConnection>();

    useEffect(
        () => {
          const newConnection = new HubConnectionBuilder()
            .withUrl(blenderAgentUrl, { accessTokenFactory: () => token }) 
            .withAutomaticReconnect()
            .build();
    
          setConnection(newConnection);
    
          return () => {
            if (newConnection.state === HubConnectionState.Connected)
              newConnection.stop();
          };
        },
        [token, blenderAgentUrl],
      );

    const handlers = useMemo<Method[]>(
        () => {
            return [
                { name: 'GetState', handler: onGettingStatus },
                { name: 'StateTransitionRequested', handler: onStateTransitions },
            ] as Method[];
        },
        [onGettingStatus, onStateTransitions],
    );

    useEffect(
        () => {
            if(connection){
                handlers.forEach(
                    m => connection.on(m.name, m.handler)
                    );
                connection.onclose(() => dispatch(updateMessageState({ id: (new Date()).getTime(), timeStamp: new Date().toDateString(), text: 'Connection to the server failed. Please check your internet connection and refresh this page.', link: '', })));
                connection.onreconnecting(() => dispatch(updateMessageState({ id: (new Date()).getTime(), timeStamp: new Date().toDateString(), text: 'Connection to the server lost. Trying to reconnect...', link: '' })));
                connection.onreconnected(() => dispatch(updateMessageState({ id: (new Date()).getTime(), timeStamp: new Date().toDateString(), text: 'Connection the server has been re- established', link: '' })));         
            }
        },
        [dispatch, handlers, connection],
    );

    const start = useCallback(
        async () => {
            if (connection != null && connection.state === HubConnectionState.Disconnected) {
                dispatch(updateConnectionStatus('connecting'));
                try {
                    await connection.start();
                    dispatch(updateConnectionStatus('connected'));
                }
                catch {
                    dispatch(updateConnectionStatus('disconnected'));
                }
            }
            return connection;
        },
        [dispatch, connection],
    );

    return useMemo<IBlenderHub>(
        () => ({
            start
        }),
        [start],
    );
};

export default ConsumeBlenderHub;